// ============================================================
// AdoptionPage.js  –  Full adoption hub with create + browse
// ============================================================
import React, { useState, useEffect, useRef } from 'react';
import {
  Heart, MapPin, MessageCircle, Plus, X, ImagePlus,
  ChevronLeft, ChevronRight, Search, SlidersHorizontal,
  CheckCircle2, AlertCircle, Info, Star, Upload, ArrowRight
} from 'lucide-react';
import { useAPI } from '../../../hooks/useAPI';
import { useAuth } from '../../../hooks/useAuth';
import LocationPicker from '../Search/LocationPicker';
import { API_URL, MAX_FILE_SIZE } from '../../../utils/constants';

// ─── constants ──────────────────────────────────────────────
const DOG_BREEDS = [
  'Unknown / Mixed','Labrador Retriever','Golden Retriever','German Shepherd',
  'French Bulldog','Bulldog','Poodle','Beagle','Dachshund','Siberian Husky',
  'Australian Shepherd','Rottweiler','Boxer','Great Dane','Chihuahua',
  'Shih Tzu','Border Collie','Cocker Spaniel','Doberman','Maltese',
  'Yorkshire Terrier','Pomeranian','Corgi','Pitbull','Other',
];
const CAT_BREEDS = [
  'Unknown / Mixed','Persian','Maine Coon','Siamese','Ragdoll','Bengal',
  'British Shorthair','Sphynx','Scottish Fold','Abyssinian',
  'Russian Blue','Birman','Norwegian Forest','Burmese','Other',
];
const PET_COLORS = [
  'Black','White','Brown','Golden / Tan','Orange / Red','Gray / Silver',
  'Cream / Beige','Tricolor','Black & White','Brown & White',
  'Black & Tan','Spotted / Speckled','Striped / Tabby','Other',
];
const PET_SIZES = ['Tiny (< 5 lbs)','Small (5–20 lbs)','Medium (20–50 lbs)','Large (50–100 lbs)','Extra Large (> 100 lbs)'];

// ─── helpers ─────────────────────────────────────────────────
const ToggleChip = ({ label, active, onClick, colorClass = 'bg-emerald-500' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-all duration-150
      ${active ? `${colorClass} text-white border-transparent shadow` : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'}`}
  >
    {active && <CheckCircle2 size={14} />}
    {label}
  </button>
);

const BoolField = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</span>
    <div className="flex gap-2">
      {[
        { v: true,  l: 'Yes', cls: 'bg-emerald-500' },
        { v: false, l: 'No',  cls: 'bg-rose-500' },
        { v: null,  l: '?',   cls: 'bg-gray-400' },
      ].map(({ v, l, cls }) => (
        <button key={l} type="button" onClick={() => onChange(v)}
          className={`px-3 py-1 rounded-full text-sm font-bold border-2 transition
            ${value === v ? `${cls} text-white border-transparent` : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'}`}>
          {l}
        </button>
      ))}
    </div>
  </div>
);

// ─── adoption badge helpers ──────────────────────────────────
const TruthBadge = ({ label, value, icon }) => {
  if (value === null || value === undefined) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold
      ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
      {icon}{' '}{value ? label : `Not ${label}`}
    </span>
  );
};

const SizeBadge = ({ size }) => {
  if (!size) return null;
  const colors = {
    'Tiny': 'bg-violet-100 text-violet-800',
    'Small': 'bg-blue-100 text-blue-800',
    'Medium': 'bg-amber-100 text-amber-800',
    'Large': 'bg-orange-100 text-orange-800',
    'Extra': 'bg-rose-100 text-rose-800',
  };
  const key = Object.keys(colors).find(k => size.startsWith(k)) || 'Medium';
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${colors[key]}`}>📏 {size}</span>;
};

// ─── Image Carousel ──────────────────────────────────────────
const ImageCarousel = ({ urls }) => {
  const [idx, setIdx] = useState(0);
  if (!urls || urls.length === 0) {
    return (
      <div className="w-full aspect-[4/3] bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center rounded-t-2xl">
        <span className="text-6xl">🐾</span>
      </div>
    );
  }
  return (
    <div className="relative w-full aspect-[4/3] bg-gray-100 rounded-t-2xl overflow-hidden group">
      <img src={urls[idx]} alt="Pet" className="w-full h-full object-cover transition-opacity duration-300" onError={e => { e.target.style.display='none'; }} />
      {urls.length > 1 && (
        <>
          <button onClick={() => setIdx((idx - 1 + urls.length) % urls.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition z-10">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => setIdx((idx + 1) % urls.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition z-10">
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
            {urls.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition ${i === idx ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Pet Card ─────────────────────────────────────────────────
const PetCard = ({ post, onContact }) => {
  const [liked, setLiked] = useState(post.liked || false);
  const [likes, setLikes] = useState(post.likes_count || 0);
  const { apiCall } = useAPI();
  const { user, isAnonymous } = useAuth();

  const extras = post.adoption_extras || {};
  const age = post.pet_age || extras.pet_age;
  const ageUnit = post.pet_age_unit || extras.pet_age_unit || 'years';
  const fee = post.adoption_fee || extras.adoption_fee;

  const handleLike = async () => {
    if (!user) return;
    try {
      if (liked) {
        await apiCall(`/posts/${post.id}/unlike`, { method: 'DELETE' });
        setLikes(l => l - 1); setLiked(false);
      } else {
        await apiCall(`/posts/${post.id}/like`, { method: 'POST' });
        setLikes(l => l + 1); setLiked(true);
      }
    } catch {}
  };

  return (
    <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col border border-gray-100">
      <div className="relative">
        <ImageCarousel urls={post.media_urls} />

        {/* Status ribbon */}
        {post.status === 'adopted' && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-t-2xl">
            <div className="bg-emerald-500 text-white font-black text-2xl px-6 py-2 rounded-full rotate-[-8deg] shadow-lg">
              ADOPTED! 🎉
            </div>
          </div>
        )}

        {/* Fee badge */}
        <div className="absolute top-3 right-3">
          {fee && fee !== '0' && fee.toLowerCase() !== 'free' ? (
            <span className="bg-white/90 backdrop-blur text-gray-800 text-xs font-bold px-2.5 py-1 rounded-full shadow">
              💰 ${fee}
            </span>
          ) : (
            <span className="bg-emerald-500/90 backdrop-blur text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">
              FREE
            </span>
          )}
        </div>

        {/* Heart */}
        <button onClick={handleLike}
          className="absolute top-3 left-3 bg-white/90 backdrop-blur rounded-full p-1.5 shadow transition hover:scale-110">
          <Heart size={18} className={liked ? 'fill-rose-500 text-rose-500' : 'text-gray-400'} />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        {/* Name + type */}
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-xl font-black text-gray-900 leading-tight">
              {post.pet_name || 'Unknown'}
            </h3>
            <p className="text-sm text-gray-500 capitalize">
              {post.pet_type || 'Pet'}
              {post.breed && post.breed !== 'Unknown' && post.breed !== 'Unknown / Mixed' && ` · ${post.breed}`}
            </p>
          </div>
          {age && <div className="text-right text-sm font-semibold text-gray-700">{age} {ageUnit}</div>}
        </div>

        {/* Sex + location */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
          {post.sex && post.sex !== 'unknown' && (
            <span>{post.sex === 'male' ? '♂ Male' : '♀ Female'}</span>
          )}
          {post.location_name && (
            <><span>·</span><MapPin size={11} /><span className="truncate">{post.location_name}</span></>
          )}
        </div>

        {/* Trait badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <SizeBadge size={post.size || extras.size} />
          {(post.color || extras.color) && (
            <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-full text-xs font-bold">🎨 {post.color || extras.color}</span>
          )}
          <TruthBadge label="Vaccinated"   value={post.vaccinated   ?? extras.vaccinated}   icon="💉" />
          <TruthBadge label="Neutered"     value={post.neutered     ?? extras.neutered}     icon="✂️" />
          <TruthBadge label="Microchipped" value={post.microchipped ?? extras.microchipped} icon="📡" />
          <TruthBadge label="Kid-friendly" value={post.good_with_kids ?? extras.good_with_kids} icon="👶" />
          <TruthBadge label="Pet-friendly" value={post.good_with_pets ?? extras.good_with_pets} icon="🐾" />
          <TruthBadge label="House trained" value={post.house_trained ?? extras.house_trained} icon="🏠" />
        </div>

        {/* Description */}
        <p className="text-sm text-gray-700 line-clamp-3 flex-1 mb-3">{post.content}</p>

        {/* Owner message */}
        {post.owner_message && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            <p className="text-xs text-amber-800 line-clamp-2">💬 {post.owner_message}</p>
          </div>
        )}

        {/* Special needs */}
        {(post.special_needs || extras.special_needs) && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-2">
            <AlertCircle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-rose-800">{post.special_needs || extras.special_needs}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-2 mt-auto pt-3 border-t border-gray-100">
          <button
            onClick={() => onContact(post)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition shadow-sm hover:shadow-md"
          >
            <MessageCircle size={16} />
            Inquire
          </button>
          <button className="flex items-center justify-center px-3 py-2.5 border-2 border-gray-200 hover:border-rose-300 text-gray-500 hover:text-rose-500 rounded-xl transition">
            <Heart size={16} />
          </button>
        </div>

        {/* Author */}
        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-50">
          <div className="w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center text-white text-xs font-bold">
            {post.author?.username?.[0]?.toUpperCase()}
          </div>
          <p className="text-xs text-gray-400">
            by <span className="font-semibold text-gray-600">{post.author?.username}</span>
            {' · '}{new Date(post.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Contact Modal ────────────────────────────────────────────
const ContactModal = ({ post, onClose }) => {
  if (!post) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-xl font-black">Inquire about {post.pet_name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
        </div>
        <div className="p-6 space-y-4">
          {post.contact_info && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-sm font-bold text-emerald-900 mb-1">📞 Contact Details</p>
              <p className="text-emerald-800 font-semibold">{post.contact_info}</p>
            </div>
          )}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-bold text-blue-900 mb-1">👤 Posted by</p>
            <p className="text-blue-800">{post.author?.username}</p>
          </div>
          <p className="text-sm text-gray-500 text-center">
            Reach out directly to the poster. Be kind and patient — they want their pet to find the perfect home!
          </p>
        </div>
        <div className="p-6 pt-0">
          <button onClick={onClose}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition">
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Create Adoption Post Form ────────────────────────────────
const CreateAdoptionForm = ({ onSuccess, onCancel }) => {
  const { isAnonymous } = useAuth();
  const [step, setStep] = useState(1); // 1 = pet info, 2 = health & traits, 3 = photos & contact
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [location, setLocation] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const [form, setForm] = useState({
    pet_name: '', pet_type: '', breed: 'Unknown / Mixed', sex: 'unknown',
    color: '', size: '', pet_age: '', pet_age_unit: 'months',
    content: '', owner_message: '', contact_info: '', adoption_fee: '0',
    vaccinated: null, neutered: null, microchipped: null,
    good_with_kids: null, good_with_pets: null, house_trained: null,
    special_needs: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleMedia = e => {
    const files = Array.from(e.target.files);
    const existing = mediaFiles.reduce((s, f) => s + f.size, 0);
    const incoming = files.reduce((s, f) => s + f.size, 0);
    if (existing + incoming > MAX_FILE_SIZE) { alert('Total exceeds 25 MB limit'); return; }
    setMediaFiles(prev => [...prev, ...files]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!form.pet_type) { setError('Please select a pet type'); return; }
    if (!form.content.trim()) { setError('Please add a description'); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append('content', form.content);
    fd.append('category', 'adoption');
    if (form.owner_message) fd.append('owner_message', form.owner_message);
    if (form.pet_name)    fd.append('pet_name', form.pet_name);
    if (form.pet_type)    fd.append('pet_type', form.pet_type);
    if (form.breed)       fd.append('breed', form.breed);
    if (form.sex)         fd.append('sex', form.sex);
    if (form.contact_info) fd.append('contact_info', form.contact_info);
    if (location) {
      fd.append('latitude', location.latitude.toString());
      fd.append('longitude', location.longitude.toString());
      fd.append('location_name', location.name);
    }
    // Adoption extras
    fd.append('pet_age', form.pet_age);
    fd.append('pet_age_unit', form.pet_age_unit);
    fd.append('color', form.color);
    fd.append('size', form.size);
    fd.append('adoption_fee', form.adoption_fee);
    if (form.special_needs) fd.append('special_needs', form.special_needs);
    if (form.vaccinated   !== null) fd.append('vaccinated',    String(form.vaccinated));
    if (form.neutered     !== null) fd.append('neutered',      String(form.neutered));
    if (form.microchipped !== null) fd.append('microchipped',  String(form.microchipped));
    if (form.good_with_kids  !== null) fd.append('good_with_kids',  String(form.good_with_kids));
    if (form.good_with_pets  !== null) fd.append('good_with_pets',  String(form.good_with_pets));
    if (form.house_trained   !== null) fd.append('house_trained',   String(form.house_trained));
    mediaFiles.forEach(f => fd.append('files', f));

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/posts/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || 'Upload failed');
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const breeds = form.pet_type === 'dog' ? DOG_BREEDS : form.pet_type === 'cat' ? CAT_BREEDS : [];
  const stepLabels = ['Pet Info', 'Health & Traits', 'Photos & Contact'];

  return (
    <>
      <div className="bg-white rounded-2xl shadow-2xl border border-emerald-100 overflow-hidden">
        {/* Step indicator */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-black text-xl">🐾 List a Pet for Adoption</h2>
            {isAnonymous && (
              <span className="text-xs bg-amber-400 text-amber-900 font-bold px-2 py-1 rounded-full">
                Guest posting
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {stepLabels.map((label, i) => (
              <button key={i} type="button" onClick={() => setStep(i + 1)}
                className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-bold transition
                  ${step === i + 1 ? 'bg-white text-emerald-700 shadow' : step > i + 1 ? 'bg-emerald-300 text-emerald-900' : 'bg-emerald-600 text-emerald-100'}`}>
                {step > i + 1 ? '✓ ' : `${i + 1}. `}{label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-rose-50 border border-rose-300 text-rose-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
              <AlertCircle size={16} />{error}
            </div>
          )}

          {/* ── STEP 1: Pet Info ── */}
          {step === 1 && (
            <div className="space-y-5 animate-[fadeIn_.2s_ease]">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Pet Name</label>
                  <input value={form.pet_name} onChange={e => set('pet_name', e.target.value)}
                    placeholder="e.g. Luna" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Pet Type <span className="text-rose-500">*</span></label>
                  <select value={form.pet_type} onChange={e => { set('pet_type', e.target.value); set('breed', 'Unknown / Mixed'); }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none">
                    <option value="">Select type…</option>
                    <option value="dog">🐕 Dog</option>
                    <option value="cat">🐈 Cat</option>
                    <option value="bird">🦜 Bird</option>
                    <option value="rabbit">🐰 Rabbit</option>
                    <option value="other">🐾 Other</option>
                  </select>
                </div>
              </div>

              {breeds.length > 0 && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Breed</label>
                  <select value={form.breed} onChange={e => set('breed', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none">
                    {breeds.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Sex</label>
                  <div className="flex gap-2 flex-wrap">
                    {[['male','♂ Male'],['female','♀ Female'],['unknown','Unknown']].map(([v,l]) => (
                      <ToggleChip key={v} label={l} active={form.sex === v} onClick={() => set('sex', v)} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Size</label>
                  <select value={form.size} onChange={e => set('size', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none">
                    <option value="">Select…</option>
                    {PET_SIZES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Age</label>
                  <div className="flex gap-2">
                    <input type="number" min="0" value={form.pet_age} onChange={e => set('pet_age', e.target.value)}
                      placeholder="e.g. 2" className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none" />
                    <select value={form.pet_age_unit} onChange={e => set('pet_age_unit', e.target.value)}
                      className="px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none text-sm">
                      <option value="weeks">wks</option>
                      <option value="months">mos</option>
                      <option value="years">yrs</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Color / Coat</label>
                  <select value={form.color} onChange={e => set('color', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none">
                    <option value="">Select…</option>
                    {PET_COLORS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Adoption Fee</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-bold">$</span>
                  <input type="number" min="0" value={form.adoption_fee} onChange={e => set('adoption_fee', e.target.value)}
                    className="w-32 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none" />
                  <span className="text-gray-400 text-sm">Enter 0 for free</span>
                </div>
              </div>

              <button type="button" onClick={() => setStep(2)}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition">
                Next: Health & Traits <ArrowRight size={18} />
              </button>
            </div>
          )}

          {/* ── STEP 2: Health & Traits ── */}
          {step === 2 && (
            <div className="space-y-5 animate-[fadeIn_.2s_ease]">
              <div className="grid grid-cols-2 gap-4">
                <BoolField label="Vaccinated"    value={form.vaccinated}    onChange={v => set('vaccinated', v)} />
                <BoolField label="Neutered/Spayed" value={form.neutered}   onChange={v => set('neutered', v)} />
                <BoolField label="Microchipped"  value={form.microchipped}  onChange={v => set('microchipped', v)} />
                <BoolField label="House Trained" value={form.house_trained} onChange={v => set('house_trained', v)} />
                <BoolField label="Good with Kids" value={form.good_with_kids} onChange={v => set('good_with_kids', v)} />
                <BoolField label="Good with Pets" value={form.good_with_pets} onChange={v => set('good_with_pets', v)} />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">
                  Special Needs <span className="text-gray-400 font-normal">(medical conditions, diet, etc.)</span>
                </label>
                <textarea value={form.special_needs} onChange={e => set('special_needs', e.target.value)}
                  placeholder="Leave empty if none…"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none" rows={2} />
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:border-gray-400 transition">
                  ← Back
                </button>
                <button type="button" onClick={() => setStep(3)}
                  className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition">
                  Next: Photos <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Photos & Contact ── */}
          {step === 3 && (
            <div className="space-y-5 animate-[fadeIn_.2s_ease]">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">
                  Description <span className="text-rose-500">*</span>
                </label>
                <textarea value={form.content} onChange={e => set('content', e.target.value)}
                  placeholder="Tell us about this pet's personality, history, and why they'd make a wonderful companion…"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none" rows={4} required />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Message from You (optional)</label>
                <textarea value={form.owner_message} onChange={e => set('owner_message', e.target.value)}
                  placeholder="Any extra context — e.g. 'I'm rehoming due to moving, this pet is very bonded to humans…'"
                  className="w-full px-4 py-3 border border-amber-300 bg-amber-50 rounded-xl focus:ring-2 focus:ring-amber-400 outline-none text-sm" rows={2} />
              </div>

              {/* Photos */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Photos</label>
                <input type="file" accept="image/*,video/*" multiple onChange={handleMedia} className="hidden" id="adopt-media" />
                <label htmlFor="adopt-media"
                  className="flex items-center justify-center gap-3 px-4 py-4 border-2 border-dashed border-emerald-300 rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition">
                  <Upload size={24} className="text-emerald-500" />
                  <span className="text-emerald-700 font-semibold">Add photos / videos</span>
                  <span className="text-xs text-gray-400">Max 25 MB</span>
                </label>
                {mediaFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {mediaFiles.map((f, i) => (
                      <div key={i} className="relative group w-20 h-20">
                        <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-lg" />
                        <button type="button" onClick={() => setMediaFiles(prev => prev.filter((_, j) => j !== i))}
                          className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Location</label>
                <button type="button" onClick={() => setShowLocationPicker(true)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 font-medium transition text-sm
                    ${location ? 'bg-emerald-50 border-emerald-400 text-emerald-800' : 'bg-white border-gray-300 text-gray-600 hover:border-emerald-400'}`}>
                  <MapPin size={18} />
                  {location ? location.name : 'Pick location on map (optional)'}
                  {location && <button type="button" onClick={e => { e.stopPropagation(); setLocation(null); }} className="ml-auto text-gray-400 hover:text-rose-500"><X size={14} /></button>}
                </button>
              </div>

              {/* Contact */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Contact Info</label>
                <input value={form.contact_info} onChange={e => set('contact_info', e.target.value)}
                  placeholder="Phone number or email…"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-400 outline-none" />
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:border-gray-400 transition">
                  ← Back
                </button>
                <button type="submit" disabled={uploading}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black rounded-xl flex items-center justify-center gap-2 transition shadow-md disabled:opacity-50">
                  {uploading ? 'Posting…' : '🐾 Post for Adoption'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {showLocationPicker && (
        <LocationPicker
          onLocationSelected={loc => { setLocation(loc); setShowLocationPicker(false); }}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </>
  );
};

// ─── Main AdoptionPage ────────────────────────────────────────
const AdoptionPage = ({ user }) => {
  const { apiCall } = useAPI();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [contactPet, setContactPet] = useState(null);
  const [filter, setFilter] = useState({ type: 'all', sex: 'all', size: '' });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const loadPets = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/posts?category=adoption&page_size=100');
      setPets(data.posts || []);
    } catch {
      setPets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPets(); }, []);

  const handlePostSuccess = () => {
    setShowForm(false);
    loadPets();
  };

  // Filter + search
  const filtered = pets
    .filter(p => filter.type === 'all' || p.pet_type === filter.type)
    .filter(p => filter.sex === 'all' || p.sex === filter.sex)
    .filter(p => !filter.size || (p.size || p.adoption_extras?.size || '').startsWith(filter.size))
    .filter(p => !search || [p.pet_name, p.content, p.breed, p.pet_type].join(' ').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => sortBy === 'newest'
      ? new Date(b.created_at) - new Date(a.created_at)
      : new Date(a.created_at) - new Date(b.created_at));

  const typeCounts = { all: pets.length };
  pets.forEach(p => { typeCounts[p.pet_type] = (typeCounts[p.pet_type] || 0) + 1; });

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-2xl shadow-xl p-8 md:p-12 mb-8 overflow-hidden">
        <div className="absolute -top-8 -right-8 w-48 h-48 bg-white/10 rounded-full" />
        <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-white/5 rounded-full" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-4xl md:text-5xl font-black mb-2">❤️ Adopt a Pet</h1>
            <p className="text-emerald-100 text-lg">Find your perfect furry companion. Every pet deserves a loving home.</p>
            <div className="flex gap-4 mt-4 text-sm">
              <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full font-semibold">{pets.length} pets available</span>
              <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full font-semibold">{typeCounts['dog'] || 0} dogs</span>
              <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full font-semibold">{typeCounts['cat'] || 0} cats</span>
            </div>
          </div>
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-3 px-8 py-4 bg-white text-emerald-700 hover:bg-emerald-50 rounded-2xl font-black text-lg shadow-lg hover:shadow-xl transition group flex-shrink-0">
            <Plus size={24} className="group-hover:rotate-90 transition-transform" />
            {showForm ? 'Cancel' : 'List a Pet'}
          </button>
        </div>
      </div>

      {/* Create form (collapsible) */}
      {showForm && (
        <div className="mb-8">
          <CreateAdoptionForm onSuccess={handlePostSuccess} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6 flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="flex items-center gap-2 flex-1 min-w-48 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
          <Search size={16} className="text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, breed…"
            className="bg-transparent outline-none text-sm flex-1 placeholder-gray-400" />
        </div>

        {/* Type filter */}
        <div className="flex gap-1.5 flex-wrap">
          {[['all','All 🐾'], ['dog','Dogs 🐕'], ['cat','Cats 🐈'], ['bird','Birds 🦜'], ['rabbit','Rabbits 🐰'], ['other','Other']].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(f => ({ ...f, type: v }))}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition
                ${filter.type === v ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'}`}>
              {l}{v !== 'all' && typeCounts[v] ? ` (${typeCounts[v]})` : ''}
            </button>
          ))}
        </div>

        {/* Sort */}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none text-gray-600 bg-white ml-auto">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mx-auto mb-4" />
            <p className="text-gray-500">Loading adorable pets…</p>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-16 text-center border border-gray-100">
          <div className="text-6xl mb-4">🐾</div>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No pets found</h3>
          <p className="text-gray-500 mb-6">Try adjusting your filters or be the first to list a pet!</p>
          <button onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition">
            List a Pet for Adoption
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-4">{filtered.length} pet{filtered.length !== 1 ? 's' : ''} available</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map(post => (
              <PetCard key={post.id} post={post} onContact={setContactPet} />
            ))}
          </div>
        </>
      )}

      {/* Contact modal */}
      <ContactModal post={contactPet} onClose={() => setContactPet(null)} />

      {/* CSS for animation */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default AdoptionPage;